import { createFileRoute, notFound } from "@tanstack/react-router"
import { createServerFn } from "@tanstack/react-start"
import Markdown from "react-markdown"
import rehypeRaw from "rehype-raw"
import remarkGfm from "remark-gfm"
import { DocsLayout } from "@/components/docs/docs-layout"
import { mdxComponents } from "@/components/docs/mdx-components"
import { DOCS_LANGUAGES } from "@/i18n/constants"
import { authFunctionMiddleware } from "@/lib/auth-middleware"
import { transformCallouts } from "@/lib/callout-transform"
import { getPage, getPageTree } from "@/lib/docs-source"

// Validation constants for input limits
const MAX_LANG_LENGTH = 10
const MAX_SLUG_LENGTH = 500

// Validate language parameter
const validateLang = (lang: string) => {
  if (!(DOCS_LANGUAGES as readonly string[]).includes(lang)) {
    throw notFound()
  }
  return lang
}

// Server function to load doc content
export const loadDocContent = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .inputValidator((input: { lang: string; slug: string }) => {
    // Validate input exists and is correct type
    if (!input || typeof input !== "object") {
      throw notFound()
    }

    const { lang, slug } = input

    // Validate types and basic constraints
    if (!lang || typeof lang !== "string" || lang.length > MAX_LANG_LENGTH) {
      throw notFound()
    }

    if (typeof slug !== "string" || slug.length > MAX_SLUG_LENGTH) {
      throw notFound()
    }

    validateLang(lang)
    return { lang, slug }
  })
  .handler(async ({ data: { lang, slug } }) => {
    // Convert slug to array of path segments
    const slugs = slug.split("/").filter(Boolean)

    // Get the page from the docs source
    const page = getPage(lang, slugs)

    if (!page) {
      throw notFound()
    }

    return {
      title: page.title,
      description: page.description,
      content: page.content,
      slug,
    }
  })

// Server function to load navigation tree
export const loadNavTree = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .inputValidator((lang: string) => {
    validateLang(lang)
    return lang
  })
  .handler(async ({ data: lang }) => {
    return getPageTree(lang)
  })

export const Route = createFileRoute("/(authenticated)/docs/$lang/$")({
  component: DocPage,
  loader: async ({ params }) => {
    const slug = params._splat || ""
    const [content, navTree] = await Promise.all([
      loadDocContent({ data: { lang: params.lang, slug } }),
      loadNavTree({ data: params.lang }),
    ])
    return { ...content, navTree }
  },
})

function DocPage() {
  const data = Route.useLoaderData()
  const { lang, _splat } = Route.useParams()
  const currentPath = `/docs/${lang}/${_splat || ""}`

  // Transform Callout JSX to HTML divs for rehype-raw
  const processedContent = transformCallouts(data.content)

  return (
    <DocsLayout currentPath={currentPath} lang={lang} navTree={data.navTree}>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <article className="prose prose-slate dark:prose-invert max-w-none">
          <Markdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={mdxComponents}
          >
            {processedContent}
          </Markdown>
        </article>
      </div>
    </DocsLayout>
  )
}
